import React, { createContext, useContext, ReactNode } from 'react';
import { useOktaAuth } from '@okta/okta-react';
import {
  DEFAULT_FEATURE_FLAGS,
  FeatureFlags,
  isOmniverseStreamingEnabled,
  isConsumerFeatureEnabled,
} from 'src/services/featureFlagsService';
import { useFeatureFlags } from 'src/hooks/useFeatureFlags';

interface FeatureFlagsContextValue {
  flags: FeatureFlags | null;
  loading: boolean;
  isOmniverseEnabled: boolean;
  isConsumerEnabled: boolean;
  refetch: () => Promise<void>;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue>({
  flags: null,
  loading: true,
  isOmniverseEnabled: DEFAULT_FEATURE_FLAGS['pep.eiotp.kubernetes.useOmniverseStreaming'],
  isConsumerEnabled: DEFAULT_FEATURE_FLAGS['pep.eiotp.useConsumer'],
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  refetch: async () => {},
});

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const { authState } = useOktaAuth();
  const isAuthenticated = !!authState?.isAuthenticated;
  const { flags, loading, refetch } = useFeatureFlags(isAuthenticated);
  const isOmniverseEnabled = isOmniverseStreamingEnabled(flags);
  const isConsumerEnabled = isConsumerFeatureEnabled(flags);

  return (
    <FeatureFlagsContext.Provider
      value={{ flags, loading, isOmniverseEnabled, isConsumerEnabled, refetch }}
    >
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export const useFeatureFlagsContext = () => useContext(FeatureFlagsContext);
